import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Crown, Check, Star, Zap, Lock, Unlock, MessageCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Premium = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPackage, setLoadingPackage] = useState(null);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);

  useEffect(() => {
    fetchPremiumStatus();
    fetchPaymentConfig();
  }, []);

  const fetchPremiumStatus = async () => {
    try {
      const response = await axios.get(`${API}/premium/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setPremiumStatus(response.data);
    } catch (error) {
      console.error('Error fetching premium status:', error);
    }
  };

  const fetchPaymentConfig = async () => {
    try {
      const response = await axios.get(`${API}/payment/config`);
      setPaymentConfig(response.data);
    } catch (error) {
      console.error('Error fetching payment config:', error);
    }
  };

  const handlePayment = async (packageId) => {
    // Vérifier si l'utilisateur est connecté AVANT de payer
    if (!user || !token) {
      // Sauvegarder le package souhaité pour après la connexion
      localStorage.setItem('pendingPurchase', packageId);
      toast.info('Connectez-vous pour effectuer le paiement');
      navigate('/auth');
      return;
    }
    
    setIsLoading(true);
    setLoadingPackage(packageId);
    
    try {
      const response = await axios.post(`${API}/payment/create`, {
        package_id: packageId,
        return_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.approval_url) {
        // Redirect to PayPal
        window.location.href = response.data.approval_url;
      } else {
        toast.error('Erreur lors de la création du paiement');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        // Token expiré ou invalide
        localStorage.setItem('pendingPurchase', packageId);
        toast.info('Session expirée. Veuillez vous reconnecter.');
        navigate('/auth');
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
      }
    } finally {
      setIsLoading(false);
      setLoadingPackage(null);
    }
  };

  const handleWhatsAppContact = (packageId) => {
    const packageName = packageId === 'monthly' ? 'Mensuel (2,99€)' : 'À vie (24,99€)';
    const message = encodeURIComponent(
      `Bonjour ! Je souhaite activer mon accès Premium MoroccoTalk.\n\nPackage: ${packageName}\n\nMerci !`
    );
    window.open(`https://wa.me/${paymentConfig?.whatsapp_number?.replace('+', '')}?text=${message}`, '_blank');
  };

  const isPremium = premiumStatus?.is_premium;
  const premiumType = premiumStatus?.premium_type;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-charcoal" />
          </button>
          <h1 className="font-playfair text-2xl font-bold text-charcoal flex items-center gap-2">
            <Crown className="w-6 h-6 text-amber-500" />
            Premium
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Already Premium */}
        {isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl p-8 mb-8 text-white text-center"
          >
            <Crown className="w-16 h-16 mx-auto mb-4" />
            <h2 className="font-playfair text-3xl font-bold mb-2">
              Vous êtes Premium ! 🎉
            </h2>
            <p className="font-nunito text-white/90">
              {premiumType === 'admin' 
                ? 'Accès Admin - Tout le contenu est débloqué' 
                : premiumType === 'lifetime' 
                  ? 'Accès à vie - Tout le contenu est débloqué pour toujours'
                  : 'Abonnement mensuel - Tout le contenu est débloqué'}
            </p>
            {premiumStatus?.premium_expires && premiumType === 'monthly' && (
              <p className="font-nunito text-white/70 mt-2">
                Expire le : {new Date(premiumStatus.premium_expires).toLocaleDateString('fr-FR')}
              </p>
            )}
          </motion.div>
        )}

        {/* Pricing Cards */}
        {!isPremium && (
          <>
            <div className="text-center mb-12">
              <h2 className="font-playfair text-4xl font-bold text-charcoal mb-4">
                Débloquez Tout le Contenu
              </h2>
              <p className="font-nunito text-lg text-charcoal/70 max-w-2xl mx-auto">
                Accédez à tout le vocabulaire, les leçons de grammaire et les situations de la vie réelle.
                <br />
                <strong>Niveau 2 et Niveau 3</strong> sont réservés aux membres Premium !
              </p>
            </div>

            {/* Sandbox Notice */}
            {paymentConfig?.mode === 'sandbox' && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-4 mb-6 text-center">
                <p className="font-nunito text-yellow-800 text-sm">
                  🧪 <strong>Mode Test</strong> - Utilisez un compte PayPal Sandbox pour tester
                </p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Monthly Plan */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl shadow-xl border-2 border-stone-200 overflow-hidden"
              >
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="w-6 h-6 text-blue-500" />
                    <span className="font-nunito font-bold text-blue-500">Mensuel</span>
                  </div>
                  <div className="mb-6">
                    <span className="font-playfair text-5xl font-bold text-charcoal">2,99€</span>
                    <span className="font-nunito text-charcoal/60">/mois</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {[
                      'Accès complet Niveau 1, 2 et 3',
                      'Toutes les catégories de vocabulaire',
                      'Toutes les leçons de grammaire',
                      'Toutes les situations réelles',
                      'Prononciations audio',
                      'Annulez à tout moment'
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 font-nunito text-charcoal/80">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handlePayment('monthly')}
                    disabled={isLoading}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-nunito font-bold py-4 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="buy-monthly-btn"
                  >
                    {loadingPackage === 'monthly' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Redirection vers PayPal...
                      </>
                    ) : (
                      'Payer avec PayPal'
                    )}
                  </button>
                  <button
                    onClick={() => handleWhatsAppContact('monthly')}
                    className="w-full mt-3 bg-[#25D366] hover:bg-[#20bd5a] text-white font-nunito font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Ou via WhatsApp
                  </button>
                </div>
              </motion.div>

              {/* Lifetime Plan */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-xl border-2 border-amber-400 overflow-hidden relative"
              >
                <div className="absolute top-4 right-4 bg-amber-500 text-white font-nunito font-bold px-3 py-1 rounded-full text-sm">
                  MEILLEURE OFFRE
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="w-6 h-6 text-amber-500" />
                    <span className="font-nunito font-bold text-amber-600">À Vie</span>
                  </div>
                  <div className="mb-6">
                    <span className="font-playfair text-5xl font-bold text-charcoal">24,99€</span>
                    <span className="font-nunito text-charcoal/60"> une fois</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {[
                      'Accès À VIE - payez une seule fois !',
                      'Tous les niveaux débloqués',
                      'Toutes les leçons de grammaire',
                      'Toutes les situations réelles',
                      'Prononciations audio',
                      'Toutes les futures mises à jour incluses',
                      'Meilleur rapport qualité-prix'
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 font-nunito text-charcoal/80">
                        <Check className="w-5 h-5 text-amber-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handlePayment('lifetime')}
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-white font-nunito font-bold py-4 rounded-xl transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="buy-lifetime-btn"
                  >
                    {loadingPackage === 'lifetime' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Redirection vers PayPal...
                      </>
                    ) : (
                      'Payer avec PayPal'
                    )}
                  </button>
                  <button
                    onClick={() => handleWhatsAppContact('lifetime')}
                    className="w-full mt-3 bg-[#25D366] hover:bg-[#20bd5a] text-white font-nunito font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Ou via WhatsApp
                  </button>
                </div>
              </motion.div>
            </div>

            {/* Level Access Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden mb-8"
            >
              <div className="p-6 bg-stone-50 border-b border-stone-200">
                <h3 className="font-playfair text-2xl font-bold text-charcoal text-center">
                  Accès par Niveau
                </h3>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Level 1 */}
                  <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="text-3xl mb-2">🌱</div>
                    <h4 className="font-nunito font-bold text-charcoal mb-2">Niveau 1</h4>
                    <div className="flex items-center justify-center gap-1 text-green-600 font-nunito text-sm">
                      <Unlock className="w-4 h-4" />
                      Partiellement gratuit
                    </div>
                  </div>
                  
                  {/* Level 2 */}
                  <div className="text-center p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <div className="text-3xl mb-2">🌿</div>
                    <h4 className="font-nunito font-bold text-charcoal mb-2">Niveau 2</h4>
                    <div className="flex items-center justify-center gap-1 text-amber-600 font-nunito text-sm">
                      <Crown className="w-4 h-4" />
                      100% Premium
                    </div>
                  </div>
                  
                  {/* Level 3 */}
                  <div className="text-center p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <div className="text-3xl mb-2">🌳</div>
                    <h4 className="font-nunito font-bold text-charcoal mb-2">Niveau 3</h4>
                    <div className="flex items-center justify-center gap-1 text-amber-600 font-nunito text-sm">
                      <Crown className="w-4 h-4" />
                      100% Premium
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Free vs Premium Comparison */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden"
            >
              <div className="p-6 bg-stone-50 border-b border-stone-200">
                <h3 className="font-playfair text-2xl font-bold text-charcoal text-center">
                  Gratuit vs Premium
                </h3>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Free */}
                  <div>
                    <h4 className="font-nunito font-bold text-charcoal mb-4 flex items-center gap-2">
                      <Unlock className="w-5 h-5 text-green-500" />
                      Accès Gratuit
                    </h4>
                    <ul className="space-y-2">
                      {[
                        'Niveau 1 : Salutations',
                        'Niveau 1 : Famille',
                        'Niveau 1 : Expressions de base',
                        'Situations familiales'
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 font-nunito text-charcoal/70">
                          <Check className="w-4 h-4 text-green-500" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Premium */}
                  <div>
                    <h4 className="font-nunito font-bold text-charcoal mb-4 flex items-center gap-2">
                      <Lock className="w-5 h-5 text-amber-500" />
                      Premium Uniquement
                    </h4>
                    <ul className="space-y-2">
                      {[
                        'Tout le Niveau 1 avancé',
                        'Niveau 2 complet',
                        'Niveau 3 complet',
                        'Toutes les situations réelles',
                        'Grand Quiz & Progression'
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 font-nunito text-charcoal/70">
                          <Crown className="w-4 h-4 text-amber-500" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}

        {/* Payment Security */}
        <div className="mt-8 text-center">
          <p className="font-nunito text-sm text-charcoal/50">
            🔒 Paiement sécurisé via PayPal
          </p>
        </div>
      </main>
    </div>
  );
};

export default Premium;
